import type { Container } from 'pixi.js';
import type { SlotTrailState } from '@goodtime/shared';
import { AssetLoader } from '../../asset/AssetLoader';
import { trailLayout } from '../../logic/trail/trail';
import type { Theme } from '../../theme';
import { TrailBonusDialog, type TrailBonusSnapshot } from '../../widgets/trail/TrailBonusDialog';
import { TrailPanel, type TrailPanelSnapshot, type TrailPart } from '../../widgets/trail/TrailPanel';
import type { SceneLayoutContext } from '../SceneComponent';

/** What the trail needs from the slot scene. */
export interface TrailHost {
  /** The scene's stage, once the scene has started (null before). */
  stage: () => Container | null;
  /** The HUD: the panel sits just above it, under every pop-up. */
  hud: () => Container | null;
  theme: Theme;
  safeTop: () => number;
  /** Where the wallet bar over the canvas ends: the bonus keeps below it. */
  boardTop: () => number;
  flyCoins: (from: { x: number; y: number }, amount: number) => Promise<void>;
  syncCoins: (coins: number) => void;
  /** Cover the slot (the host's overlays make way) until released. */
  cover: () => () => void;
}

/**
 * The Sweet Trail on a slot without the road: its panel over the reels'
 * surroundings and its pick-a-prize bonus, both on the slot's canvas. The
 * host (the app) sends the trail's state and says when the panel should
 * stand aside; this places and draws it.
 */
export class TrailComponent {
  private panel: TrailPanel | null = null;
  private bonus: TrailBonusDialog | null = null;
  private now: { trail: SlotTrailState | null; spinKey: number; delay: number } = { trail: null, spinKey: 0, delay: 0 };
  private hidden = true;
  private covered = false;
  private ctx: SceneLayoutContext | null = null;
  private collectorNoun = 'lucky symbols';
  private notice: string | null = null;
  /** A tap on the gift while the bonus waits. */
  onOpenBonus: (() => void) | null = null;
  /** A hop's prize came up on the panel (where, or null off screen). */
  onPrize: ((at: { x: number; y: number } | null) => void) | null = null;

  constructor(private readonly host: TrailHost) {}

  /** A new trail state (`spinKey` changes when a spin made it); null takes
   *  the panel away. `revealDelayMs` holds the board back while a win shows. */
  setTrail(trail: SlotTrailState | null, spinKey: number, revealDelayMs = 0): void {
    this.now = { trail, spinKey, delay: revealDelayMs };
    // Before the scene has started there's nowhere to draw: the panel is
    // made at its first layout instead.
    if (trail && !this.panel) this.build();
    if (trail) this.panel?.setTrail(trail, spinKey, revealDelayMs);
    this.refresh();
  }

  /** The host's say: hide the panel (a slot loading, the first-spin hint,
   *  a gamble), what the slot's collectors are called, and a line to show
   *  under the board in place of its hint (null for none). */
  setChrome(options: { hidden: boolean; collectorNoun?: string; notice?: string | null }): void {
    this.hidden = options.hidden;
    if (options.collectorNoun) {
      this.collectorNoun = options.collectorNoun;
      if (this.panel) this.panel.collectorNoun = options.collectorNoun;
    }
    if (options.notice !== undefined) {
      this.notice = options.notice;
      this.panel?.setNotice(options.notice);
    }
    this.refresh();
  }

  /** A pop-up, title card or bonus covers the slot: the panel steps aside. */
  setCovered(covered: boolean): void {
    this.covered = covered;
    this.refresh();
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    if (this.now.trail && !this.panel) {
      this.build()?.setTrail(this.now.trail, this.now.spinKey, 0);
      this.refresh();
    }
    this.place();
    if (this.bonus) {
      this.bonus.setTopInset(this.host.boardTop());
      this.bonus.layout(ctx.width, ctx.height);
    }
  }

  /** Play the pick-a-prize bonus over the slot; resolves with the trail
   *  as it stands when the board closes. */
  async playBonus(
    trail: SlotTrailState,
    pick: (index: number) => Promise<{ trail: SlotTrailState; coins: number | null }>,
    words: { title?: string; finishLabel?: string } = {},
  ): Promise<SlotTrailState> {
    const loader = AssetLoader.shared;
    const slot = this.host.theme.assets?.slot;
    this.bonus ??= new TrailBonusDialog((name) => {
      switch (name) {
        case 'tile':
          return loader.getTexture('shared:reel:hns_lock_tile');
        case 'box':
          return slot?.['scatter'] ? loader.getTexture(slot['scatter'].alias) : null;
        case 'coin':
          return loader.getTexture('shared:icon:coin');
        case 'gem':
          return loader.getTexture('shared:icon:gem');
        case 'floor':
          return this.floor();
      }
    });
    const stage = this.host.stage();
    if (this.bonus.visible || !stage) return trail;
    const release = this.host.cover();
    stage.addChild(this.bonus);
    this.bonus.setTopInset(this.host.boardTop());
    if (this.ctx) this.bonus.layout(this.ctx.width, this.ctx.height);
    try {
      return await this.bonus.play(trail, {
        ...words,
        pick,
        flyCoins: (from, amount) => this.host.flyCoins(from, amount),
        syncCoins: (coins) => this.host.syncCoins(coins),
      });
    } finally {
      release();
    }
  }

  get panelUi(): TrailPanelSnapshot | null {
    return this.panel?.snapshot ?? null;
  }

  get bonusUi(): TrailBonusSnapshot | null {
    return this.bonus?.snapshot ?? null;
  }

  tap(part: TrailPart): boolean {
    return this.panel?.tap(part) ?? false;
  }

  where(part: TrailPart): { x: number; y: number } | null {
    return this.panel?.where(part) ?? null;
  }

  tapBox(index: number): boolean {
    return this.bonus?.tapBox(index) ?? false;
  }

  pressBonus(label: string): boolean {
    return this.bonus?.press(label) ?? false;
  }

  whereBonus(target: number | string): { x: number; y: number } | null {
    return this.bonus?.where(target) ?? null;
  }

  destroy(): void {
    this.panel?.destroy({ children: true });
    this.panel = null;
    this.bonus?.destroy({ children: true });
    this.bonus = null;
  }

  private build(): TrailPanel | null {
    const stage = this.host.stage();
    if (!stage) return null;
    const loader = AssetLoader.shared;
    const slot = this.host.theme.assets?.slot;
    this.panel = new TrailPanel((name) => {
      switch (name) {
        case 'bezel':
          return loader.getTexture('road:light_bezel');
        case 'cobbles':
          return loader.getTexture('road:path_strip');
        case 'path': {
          // The slot's world's own road surface, if it has one.
          const own = slot?.['trail_path_own']?.alias;
          return own ? loader.getTexture(own) : null;
        }
        case 'coin':
          return loader.getTexture('shared:icon:coin');
        case 'gem':
          return loader.getTexture('shared:icon:gem');
        case 'spin':
          return loader.getTexture('shared:icon:spin');
        case 'gift':
          return loader.getTexture('shared:icon:gift');
        case 'collector':
          return slot?.['cherry'] ? loader.getTexture(slot['cherry'].alias) : null;
        case 'floor':
          return this.floor();
      }
    });
    this.panel.collectorNoun = this.collectorNoun;
    this.panel.setNotice(this.notice);
    this.panel.onOpenBonus = () => this.onOpenBonus?.();
    this.panel.onPrize = (at) => this.onPrize?.(at);
    const hud = this.host.hud();
    if (hud && hud.parent === stage) stage.addChildAt(this.panel, stage.getChildIndex(hud) + 1);
    else stage.addChild(this.panel);
    this.place();
    return this.panel;
  }

  private place(): void {
    const ctx = this.ctx;
    if (!this.panel || !ctx) return;
    this.panel.setLayout(
      trailLayout({
        width: ctx.width,
        height: ctx.height,
        safeTop: this.host.safeTop(),
        machineLeft: ctx.machineLeft ?? 0,
        machineRight: ctx.machineRight ?? ctx.width,
        machineTop: ctx.machineTop,
        spinButtonY: ctx.spinButtonY,
      }),
    );
  }

  private refresh(): void {
    if (this.panel) this.panel.visible = this.now.trail !== null && !this.hidden && !this.covered;
  }

  private floor() {
    const alias = this.host.theme.assets?.scene?.columnBackground?.alias;
    return alias ? AssetLoader.shared.getTexture(alias) : null;
  }
}
